import { useEffect, useState, useMemo, useCallback } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  CartesianGrid,
} from "recharts";
import {
  Calendar,
  Search,
  FileText,
  CheckCircle2,
  Clock,
  AlertTriangle,
  RefreshCw,
  Eye,
  X,
  ArrowUpDown,
  SlidersHorizontal,
  PhoneCall,
  CheckCheck,
  Truck,
  TrendingUp,
  Send,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { money, compactMoney } from "../../lib/format";
import { isSuperAdmin, isAdmin, getUserDepartments, DEPARTMENTS } from "../../lib/permissions";
import Pagination, { usePagination } from "./Pagination";

/**
 * Returns current date formatted as YYYY-MM-DD in IST (Asia/Kolkata, UTC+5:30)
 */
function getTodayIST() {
  const d = new Date();
  const utc = d.getTime() + d.getTimezoneOffset() * 60000;
  const istDate = new Date(utc + 3600000 * 5.5);
  return istDate.toISOString().split("T")[0];
}

function formatDateDisplay(dateStr) {
  if (!dateStr) return "—";
  try {
    const parts = dateStr.split("-");
    if (parts.length === 3) {
      const d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
      return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    }
    const d = new Date(dateStr);
    return isNaN(d.getTime())
      ? dateStr
      : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return dateStr;
  }
}

export default function Reports({ user }) {
  const isUserSuperAdmin = isSuperAdmin(user);
  const isUserAdmin = isAdmin(user);
  const canAccessTeamReports = isUserSuperAdmin || isUserAdmin;
  const userDepts = getUserDepartments(user);

  // General management reports data
  const [generalReports, setGeneralReports] = useState(null);
  const [sendingDigest, setSendingDigest] = useState(false);

  // Super Admin Team Performance state
  const [reportDate, setReportDate] = useState(getTodayIST());
  const [selectedDept, setSelectedDept] = useState("All");
  const [selectedUser, setSelectedUser] = useState("All");
  const [selectedRole, setSelectedRole] = useState("All");
  const [selectedActType, setSelectedActType] = useState("All");
  const [selectedReportStatus, setSelectedReportStatus] = useState("All");

  // Advanced filters dropdown popover toggle
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  const [teamData, setTeamData] = useState(null);
  const [loadingTeam, setLoadingTeam] = useState(false);

  // 4 Primary Daily Tabs: "overview", "activity", "reports", "departments"
  // Secondary view: "insights"
  const [activeTab, setActiveTab] = useState("overview");

  // Search & Sorting for user table
  const [userSearch, setUserSearch] = useState("");
  const [sortField, setSortField] = useState("name");
  const [sortAsc, setSortAsc] = useState(true);

  // Tab-specific filters & search
  const [activitySearch, setActivitySearch] = useState("");
  const [reportStatusFilter, setReportStatusFilter] = useState("All");
  const [selectedDeptCard, setSelectedDeptCard] = useState("All");

  // Modals
  const [viewReportUser, setViewReportUser] = useState(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submittingReport, setSubmittingReport] = useState(false);
  const [myReportForm, setMyReportForm] = useState({
    work_summary: "",
    tasks_completed_summary: "",
    tasks_pending_summary: "",
    blockers: "",
    remarks: "",
  });

  // Fetch standard management reports
  const fetchGeneralReports = useCallback(async () => {
    try {
      const res = await api("get", "/reports");
      setGeneralReports(res.data);
    } catch (e) {
      console.error("Failed to load general reports", e);
    }
  }, []);

  // Fetch Team Performance data (Super Admin & Admin)
  const fetchTeamPerformance = useCallback(async () => {
    if (!canAccessTeamReports) return;
    setLoadingTeam(true);
    try {
      const params = new URLSearchParams();
      if (reportDate) params.append("date", reportDate);
      if (selectedDept && selectedDept !== "All") params.append("department", selectedDept);
      if (selectedUser && selectedUser !== "All") params.append("user_id", selectedUser);
      if (selectedRole && selectedRole !== "All") params.append("role", selectedRole);
      if (selectedActType && selectedActType !== "All") params.append("activity_type", selectedActType);
      if (selectedReportStatus && selectedReportStatus !== "All") params.append("report_status", selectedReportStatus);

      const res = await api("get", `/reports/team-performance?${params.toString()}`);
      setTeamData(res.data);
    } catch (e) {
      console.error("Failed to load team performance", e);
      toast.error(e.response?.data?.detail || "Could not load team performance data");
    } finally {
      setLoadingTeam(false);
    }
  }, [canAccessTeamReports, reportDate, selectedDept, selectedUser, selectedRole, selectedActType, selectedReportStatus]);

  useEffect(() => {
    fetchGeneralReports();
  }, [fetchGeneralReports]);

  useEffect(() => {
    if (canAccessTeamReports) {
      fetchTeamPerformance();
    }
  }, [canAccessTeamReports, fetchTeamPerformance]);

  const openSubmitModal = async () => {
    setShowSubmitModal(true);
    if (!user || !user.user_id) return;
    try {
      const res = await api("get", `/reports/daily-reports/${user.user_id}?date=${reportDate}`);
      if (res.data?.report) {
        const r = res.data.report;
        setMyReportForm({
          work_summary: r.work_summary || "",
          tasks_completed_summary: r.tasks_completed_summary || "",
          tasks_pending_summary: r.tasks_pending_summary || "",
          blockers: r.blockers || "",
          remarks: r.remarks || "",
        });
      } else {
        setMyReportForm({
          work_summary: "",
          tasks_completed_summary: "",
          tasks_pending_summary: "",
          blockers: "",
          remarks: "",
        });
      }
    } catch {
      // Keep blank
    }
  };

  const handleSaveDailyReport = async (e) => {
    e.preventDefault();
    if (!myReportForm.work_summary.trim()) {
      toast.error("Please enter a summary of your work for today");
      return;
    }
    setSubmittingReport(true);
    try {
      await api("post", "/reports/daily-reports", {
        report_date: reportDate,
        ...myReportForm,
      });
      toast.success("Daily report submitted successfully!");
      setShowSubmitModal(false);
      if (canAccessTeamReports) {
        fetchTeamPerformance();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit daily report");
    } finally {
      setSubmittingReport(false);
    }
  };

  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const res = await api("post", "/emails/run-digest");
      if (res.data.ok) toast.success("Weekly digest emailed to admin + management");
      else toast.error(res.data.reason || "Digest could not be sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send digest");
    } finally {
      setSendingDigest(false);
    }
  };

  const handleResetFilters = () => {
    setReportDate(getTodayIST());
    setSelectedDept("All");
    setSelectedUser("All");
    setSelectedRole("All");
    setSelectedActType("All");
    setSelectedReportStatus("All");
    setUserSearch("");
    setShowMoreFilters(false);
  };

  const hasAdvancedFilters = selectedRole !== "All" || selectedActType !== "All" || selectedReportStatus !== "All";
  const hasAnyFilterActive =
    reportDate !== getTodayIST() ||
    selectedDept !== "All" ||
    selectedUser !== "All" ||
    hasAdvancedFilters ||
    userSearch.trim() !== "";

  // Filtered & sorted users list for Team Overview
  const filteredUsers = useMemo(() => {
    if (!teamData || !teamData.users) return [];
    let list = [...teamData.users];

    if (userSearch.trim()) {
      const q = userSearch.toLowerCase();
      list = list.filter(
        (u) =>
          (u.name && u.name.toLowerCase().includes(q)) ||
          (u.email && u.email.toLowerCase().includes(q)) ||
          (u.department && u.department.toLowerCase().includes(q))
      );
    }

    list.sort((a, b) => {
      let valA = a[sortField];
      let valB = b[sortField];

      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();

      if (valA === valB) return 0;
      if (valA === null || valA === undefined) return 1;
      if (valB === null || valB === undefined) return -1;

      return sortAsc ? (valA > valB ? 1 : -1) : valA < valB ? 1 : -1;
    });

    return list;
  }, [teamData, userSearch, sortField, sortAsc]);

  const {
    paginatedItems: paginatedUsers = [],
    currentPage,
    totalPages,
    setCurrentPage,
    pageSize,
    setPageSize,
  } = usePagination(filteredUsers, 10);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  // Activity feed items filtered by search
  const filteredActivityList = useMemo(() => {
    if (!teamData || !teamData.timeline) return [];
    if (!activitySearch.trim()) return teamData.timeline;
    const q = activitySearch.toLowerCase();
    return teamData.timeline.filter(
      (act) =>
        (act.user_name && act.user_name.toLowerCase().includes(q)) ||
        (act.action && act.action.toLowerCase().includes(q)) ||
        (act.entity_name && act.entity_name.toLowerCase().includes(q)) ||
        (act.entity_id && act.entity_id.toLowerCase().includes(q)) ||
        (act.type && act.type.toLowerCase().includes(q))
    );
  }, [teamData, activitySearch]);

  // Daily reports list filtered by submission status
  const filteredDailyReportsList = useMemo(() => {
    if (!teamData || !teamData.users) return [];
    if (reportStatusFilter === "Submitted") {
      return teamData.users.filter((u) => u.daily_report_status === "Submitted");
    }
    if (reportStatusFilter === "Pending") {
      return teamData.users.filter((u) => u.daily_report_status !== "Submitted");
    }
    return teamData.users;
  }, [teamData, reportStatusFilter]);

  // Loading state
  if (!generalReports || (canAccessTeamReports && !teamData)) {
    return (
      <div className="content">
        <div className="loading-screen" style={{ minHeight: 320, display: "grid", placeItems: "center" }}>
          <div style={{ textAlign: "center" }}>
            <RefreshCw className="animate-spin" size={26} style={{ color: "var(--blue)", margin: "0 auto 10px" }} />
            <div style={{ color: "var(--muted)", fontSize: 13, fontWeight: 600 }}>Loading team reports & performance…</div>
          </div>
        </div>
      </div>
    );
  }

  const kpis = teamData?.overall_kpis || {};

  return (
    <div className="content">
      {/* 1. Header Section */}
      <div className="page-heading">
        <div>
          <div className="small-label">
            {isUserSuperAdmin
              ? "SUPER ADMIN · EXECUTIVE CONTROL"
              : isUserAdmin
              ? "ADMIN · EXECUTIVE CONTROL"
              : "MANAGEMENT · REPORTS"}
          </div>
          <h1>{canAccessTeamReports ? "Daily Team Reports" : "Management Reports"}</h1>
          <p className="muted">
            {canAccessTeamReports
              ? "Track today's team activity and performance."
              : "Overview of system volume, pipeline and workspace status."}
          </p>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          {/* Submit My Daily Report */}
          <button
            className="outline-btn"
            onClick={openSubmitModal}
            data-testid="submit-my-report-btn"
          >
            <FileText size={14} /> Submit My Daily Report
          </button>

          {/* Refresh button */}
          {canAccessTeamReports && (
            <button
              className="outline-btn"
              onClick={fetchTeamPerformance}
              disabled={loadingTeam}
              title="Refresh today's data"
            >
              <RefreshCw size={14} className={loadingTeam ? "animate-spin" : ""} />
              {loadingTeam ? "Refreshing…" : "Refresh"}
            </button>
          )}

          {/* Digest Action (Admin) */}
          {isUserAdmin && (
            <button
              className="outline-btn"
              data-testid="send-digest-button"
              onClick={sendDigest}
              disabled={sendingDigest}
            >
              <Send size={14} /> {sendingDigest ? "Sending…" : "Weekly Digest"}
            </button>
          )}
        </div>
      </div>

      {canAccessTeamReports && (
        <>
          {/* 2. Top 4 Summary KPI Cards (Adaptive by Selected Department) */}
          <section style={{ marginBottom: 16 }} data-testid="daily-kpi-cards-section">
            <div className="metrics-grid">
              {selectedDept === "Sales" ? (
                <>
                  <div className="metric blue">
                    <div className="metric-top">
                      <span>CALLS TODAY</span>
                      <PhoneCall size={14} style={{ color: "var(--blue)", opacity: 0.8 }} />
                    </div>
                    <strong data-testid="kpi-calls-made">{kpis.calls_made ?? 0}</strong>
                  </div>

                  <div className="metric green">
                    <div className="metric-top">
                      <span>LEADS QUALIFIED TODAY</span>
                      <CheckCheck size={14} style={{ color: "var(--green)", opacity: 0.8 }} />
                    </div>
                    <strong data-testid="kpi-leads-qualified">{kpis.leads_qualified ?? 0}</strong>
                  </div>

                  <div className="metric green">
                    <div className="metric-top">
                      <span>LEADS WON TODAY</span>
                      <TrendingUp size={14} style={{ color: "var(--green)", opacity: 0.8 }} />
                    </div>
                    <strong data-testid="kpi-leads-won">{kpis.leads_won ?? kpis.leads_converted ?? 0}</strong>
                  </div>

                  <div className="metric green">
                    <div className="metric-top">
                      <span>CURRENT WON PIPELINE</span>
                      <CheckCircle2 size={14} style={{ color: "var(--green)", opacity: 0.8 }} />
                    </div>
                    <strong data-testid="kpi-pipeline-won">
                      {kpis.pipeline_won ?? 0}
                      <span style={{ fontSize: 13, fontWeight: 500, color: "var(--muted)", marginLeft: 6 }}>
                        ({compactMoney(kpis.pipeline_won_value || 0)})
                      </span>
                    </strong>
                  </div>
                </>
              ) : selectedDept === "Delivery" ? (
                <>
                  <div className="metric green">
                    <div className="metric-top">
                      <span>ORDERS DELIVERED TODAY</span>
                      <Truck size={14} style={{ color: "var(--green)", opacity: 0.8 }} />
                    </div>
                    <strong data-testid="kpi-orders-delivered">{kpis.orders_delivered ?? 0}</strong>
                  </div>

                  <div className="metric amber">
                    <div className="metric-top">
                      <span>DELIVERIES PENDING</span>
                      <Clock size={14} style={{ color: "var(--amber)", opacity: 0.8 }} />
                    </div>
                    <strong data-testid="kpi-deliveries-pending">{kpis.deliveries_pending ?? 0}</strong>
                  </div>

                  <div className="metric blue">
                    <div className="metric-top">
                      <span>ASSIGNED FOR DELIVERY</span>
                      <Truck size={14} style={{ color: "var(--blue)", opacity: 0.8 }} />
                    </div>
                    <strong data-testid="kpi-orders-assigned">{kpis.orders_assigned_for_delivery ?? 0}</strong>
                  </div>

                  <div className="metric green">
                    <div className="metric-top">
                      <span>CHALLANS COMPLETED</span>
                      <FileText size={14} style={{ color: "var(--green)", opacity: 0.8 }} />
                    </div>
                    <strong data-testid="kpi-challans-completed">{kpis.challans_completed ?? 0}</strong>
                  </div>
                </>
              ) : selectedDept === "Design" ? (
                <>
                  <div className="metric green">
                    <div className="metric-top">
                      <span>DESIGNS COMPLETED TODAY</span>
                      <CheckCircle2 size={14} style={{ color: "var(--green)", opacity: 0.8 }} />
                    </div>
                    <strong data-testid="kpi-designs-completed">{kpis.designs_completed ?? 0}</strong>
                  </div>

                  <div className="metric amber">
                    <div className="metric-top">
                      <span>AWAITING APPROVAL</span>
                      <Clock size={14} style={{ color: "var(--amber)", opacity: 0.8 }} />
                    </div>
                    <strong data-testid="kpi-designs-awaiting">{kpis.designs_awaiting_approval ?? 0}</strong>
                  </div>

                  <div className="metric green">
                    <div className="metric-top">
                      <span>APPROVED TODAY</span>
                      <CheckCheck size={14} style={{ color: "var(--green)", opacity: 0.8 }} />
                    </div>
                    <strong data-testid="kpi-designs-approved">{kpis.designs_approved_today ?? 0}</strong>
                  </div>

                  <div className="metric blue">
                    <div className="metric-top">
                      <span>ASSIGNED DESIGN TASKS</span>
                      <FileText size={14} style={{ color: "var(--blue)", opacity: 0.8 }} />
                    </div>
                    <strong data-testid="kpi-designs-assigned">{kpis.design_tasks_assigned ?? 0}</strong>
                  </div>
                </>
              ) : selectedDept === "Production" ? (
                <>
                  <div className="metric green">
                    <div className="metric-top">
                      <span>COMPLETED PRODUCTION TODAY</span>
                      <CheckCircle2 size={14} style={{ color: "var(--green)", opacity: 0.8 }} />
                    </div>
                    <strong data-testid="kpi-production-completed">{kpis.production_tasks_completed ?? 0}</strong>
                  </div>

                  <div className="metric amber">
                    <div className="metric-top">
                      <span>ORDERS IN PRODUCTION</span>
                      <Clock size={14} style={{ color: "var(--amber)", opacity: 0.8 }} />
                    </div>
                    <strong data-testid="kpi-orders-in-production">{kpis.orders_pending_production ?? 0}</strong>
                  </div>

                  <div className="metric blue">
                    <div className="metric-top">
                      <span>ENTERING PROD TODAY</span>
                      <TrendingUp size={14} style={{ color: "var(--blue)", opacity: 0.8 }} />
                    </div>
                    <strong data-testid="kpi-orders-entering-prod">{kpis.orders_entering_production ?? 0}</strong>
                  </div>

                  <div className="metric red">
                    <div className="metric-top">
                      <span>OVERDUE PRODUCTION WORK</span>
                      <AlertTriangle size={14} style={{ color: "var(--red)", opacity: 0.8 }} />
                    </div>
                    <strong data-testid="kpi-overdue-production" style={{ color: (kpis.overdue_production_work || 0) > 0 ? "var(--red)" : "inherit" }}>
                      {kpis.overdue_production_work ?? 0}
                    </strong>
                  </div>
                </>
              ) : selectedDept === "Procurement" ? (
                <>
                  <div className="metric green">
                    <div className="metric-top">
                      <span>PRICE RECEIVED TODAY</span>
                      <CheckCircle2 size={14} style={{ color: "var(--green)", opacity: 0.8 }} />
                    </div>
                    <strong data-testid="kpi-price-completed">{kpis.price_requests_completed ?? 0}</strong>
                  </div>

                  <div className="metric amber">
                    <div className="metric-top">
                      <span>PENDING PRICE REQUESTS</span>
                      <Clock size={14} style={{ color: "var(--amber)", opacity: 0.8 }} />
                    </div>
                    <strong data-testid="kpi-price-pending">{kpis.price_requests_pending ?? 0}</strong>
                  </div>

                  <div className="metric blue">
                    <div className="metric-top">
                      <span>ASSIGNED PRICE REQUESTS</span>
                      <FileText size={14} style={{ color: "var(--blue)", opacity: 0.8 }} />
                    </div>
                    <strong data-testid="kpi-price-assigned">{kpis.price_requests_assigned ?? 0}</strong>
                  </div>

                  <div className="metric red">
                    <div className="metric-top">
                      <span>OVERDUE REQUESTS</span>
                      <AlertTriangle size={14} style={{ color: "var(--red)", opacity: 0.8 }} />
                    </div>
                    <strong data-testid="kpi-price-overdue" style={{ color: (kpis.price_requests_overdue || 0) > 0 ? "var(--red)" : "inherit" }}>
                      {kpis.price_requests_overdue ?? 0}
                    </strong>
                  </div>
                </>
              ) : (
                <>
                  <div className="metric blue">
                    <div className="metric-top">
                      <span>CALLS TODAY</span>
                      <PhoneCall size={14} style={{ color: "var(--blue)", opacity: 0.8 }} />
                    </div>
                    <strong data-testid="kpi-calls-made">{kpis.calls_made ?? 0}</strong>
                  </div>

                  <div className="metric green">
                    <div className="metric-top">
                      <span>LEADS QUALIFIED TODAY</span>
                      <CheckCheck size={14} style={{ color: "var(--green)", opacity: 0.8 }} />
                    </div>
                    <strong data-testid="kpi-leads-qualified">{kpis.leads_qualified ?? 0}</strong>
                  </div>

                  <div className="metric green">
                    <div className="metric-top">
                      <span>ORDERS DELIVERED TODAY</span>
                      <Truck size={14} style={{ color: "var(--green)", opacity: 0.8 }} />
                    </div>
                    <strong data-testid="kpi-orders-delivered">{kpis.orders_delivered ?? 0}</strong>
                  </div>

                  <div className="metric green">
                    <div className="metric-top">
                      <span>TASKS COMPLETED TODAY</span>
                      <CheckCircle2 size={14} style={{ color: "var(--green)", opacity: 0.8 }} />
                    </div>
                    <strong data-testid="kpi-tasks-completed">{kpis.tasks_completed ?? 0}</strong>
                  </div>
                </>
              )}
            </div>

            {/* Compact Secondary Summary Strip with Clear Today vs Pipeline Labels */}
            <div
              className="panel"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                padding: "12px 18px",
                background: "#ffffff",
                fontSize: 12.5,
                gap: 12,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 6px" }}>
                <span style={{ color: "var(--muted)", fontWeight: 500 }}>Leads Created (Today)</span>
                <strong style={{ color: "var(--navy)", fontSize: 14 }} data-testid="kpi-leads-created">
                  {kpis.leads_created ?? 0}
                </strong>
              </div>

              {selectedDept === "Sales" ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 6px", borderLeft: "1px solid #f1f5f9" }}>
                  <span style={{ color: "var(--muted)", fontWeight: 500 }}>Total Assigned Leads</span>
                  <strong style={{ color: "var(--blue)", fontSize: 14 }} data-testid="kpi-pipeline-total">
                    {kpis.pipeline_total_leads ?? 0}
                  </strong>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 6px", borderLeft: "1px solid #f1f5f9" }}>
                  <span style={{ color: "var(--muted)", fontWeight: 500 }}>Leads Won (Today)</span>
                  <strong style={{ color: (kpis.leads_won || 0) > 0 ? "var(--green)" : "var(--navy)", fontSize: 14 }} data-testid="kpi-leads-won-today">
                    {kpis.leads_won ?? kpis.leads_converted ?? 0}
                  </strong>
                </div>
              )}

              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 6px", borderLeft: "1px solid #f1f5f9" }}>
                <span style={{ color: "var(--muted)", fontWeight: 500 }}>Follow-ups Due</span>
                <strong style={{ color: "var(--navy)", fontSize: 14 }} data-testid="kpi-followups-due">
                  {kpis.followups_due ?? 0}
                </strong>
              </div>

              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 6px", borderLeft: "1px solid #f1f5f9" }}>
                <span style={{ color: "var(--muted)", fontWeight: 500 }}>Overdue Tasks</span>
                <strong
                  style={{ color: (kpis.overdue_tasks || 0) > 0 ? "var(--red)" : "var(--navy)", fontSize: 14 }}
                  data-testid="kpi-overdue-tasks"
                >
                  {kpis.overdue_tasks ?? 0}
                </strong>
              </div>

              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 6px", borderLeft: "1px solid #f1f5f9" }}>
                <span style={{ color: "var(--muted)", fontWeight: 500 }}>Daily Reports</span>
                <strong
                  style={{
                    color: (kpis.daily_reports_submitted || 0) > 0 ? "var(--green)" : "var(--navy)",
                    fontSize: 14,
                  }}
                  data-testid="kpi-reports-submitted"
                >
                  {kpis.daily_reports_submitted ?? 0} / {(teamData?.users || []).length}
                </strong>
              </div>
            </div>
          </section>

          {/* 3. Compact Filter Row */}
          <div
            className="panel"
            style={{
              marginBottom: 20,
              padding: "10px 16px",
              background: "#ffffff",
            }}
            data-testid="super-admin-filter-bar"
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
                justifyContent: "space-between",
              }}
            >
              {/* Core 3 Filters: Date, Department, Team Member */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                {/* Date Input with clean styling */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    background: "#f8fafc",
                    border: "1px solid var(--line)",
                    borderRadius: 6,
                    padding: "0 10px",
                    height: 36,
                  }}
                >
                  <Calendar size={14} style={{ color: "var(--muted)" }} />
                  <input
                    type="date"
                    value={reportDate}
                    onChange={(e) => setReportDate(e.target.value)}
                    style={{
                      border: "none",
                      background: "transparent",
                      fontSize: 12.5,
                      color: "var(--navy)",
                      outline: "none",
                      fontFamily: "inherit",
                    }}
                    data-testid="filter-date-input"
                  />
                </div>

                {/* Department Dropdown */}
                <select
                  value={selectedDept}
                  onChange={(e) => {
                    setSelectedDept(e.target.value);
                    setSelectedUser("All");
                  }}
                  style={{
                    height: 36,
                    borderRadius: 6,
                    border: "1px solid var(--line)",
                    background: "#fff",
                    padding: "0 12px",
                    fontSize: 12.5,
                    color: "var(--navy)",
                  }}
                  data-testid="filter-dept-select"
                >
                  <option value="All">All Departments</option>
                  {DEPARTMENTS.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>

                {/* Team Member Dropdown */}
                <select
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  style={{
                    height: 36,
                    borderRadius: 6,
                    border: "1px solid var(--line)",
                    background: "#fff",
                    padding: "0 12px",
                    fontSize: 12.5,
                    color: "var(--navy)",
                    minWidth: 170,
                  }}
                  data-testid="filter-user-select"
                >
                  <option value="All">All Team Members ({teamData?.total_team_members || 0})</option>
                  {(teamData?.users || []).map((u) => (
                    <option key={u.user_id} value={u.user_id}>
                      {u.name} ({u.department})
                    </option>
                  ))}
                </select>

                {/* More Filters Toggle */}
                <div style={{ position: "relative" }}>
                  <button
                    type="button"
                    className="outline-btn"
                    onClick={() => setShowMoreFilters(!showMoreFilters)}
                    style={{
                      height: 36,
                      padding: "0 12px",
                      fontSize: 12,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      background: hasAdvancedFilters ? "#eff6ff" : "#ffffff",
                      borderColor: hasAdvancedFilters ? "#93c5fd" : "var(--line)",
                      color: hasAdvancedFilters ? "var(--blue)" : "var(--navy)",
                    }}
                  >
                    <SlidersHorizontal size={13} />
                    More Filters
                    {hasAdvancedFilters && (
                      <span
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          background: "var(--blue)",
                          display: "inline-block",
                        }}
                      />
                    )}
                    <ChevronDown size={12} />
                  </button>

                  {/* Popover for Advanced Filters */}
                  {showMoreFilters && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        marginTop: 6,
                        background: "#ffffff",
                        border: "1px solid var(--line)",
                        borderRadius: 8,
                        boxShadow: "0 12px 24px -4px rgba(15,23,42,0.12), 0 4px 6px -2px rgba(15,23,42,0.04)",
                        padding: 16,
                        width: 260,
                        zIndex: 100,
                        display: "flex",
                        flexDirection: "column",
                        gap: 12,
                      }}
                    >
                      <div>
                        <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", marginBottom: 4 }}>
                          Role
                        </label>
                        <select
                          className="form-control"
                          value={selectedRole}
                          onChange={(e) => setSelectedRole(e.target.value)}
                          style={{ height: 32, fontSize: 12, padding: "4px 8px" }}
                          data-testid="filter-role-select"
                        >
                          <option value="All">All Roles</option>
                          <option value="super_admin">Super Admin</option>
                          <option value="admin">Admin</option>
                          <option value="user">User</option>
                        </select>
                      </div>

                      <div>
                        <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", marginBottom: 4 }}>
                          Activity Type
                        </label>
                        <select
                          className="form-control"
                          value={selectedActType}
                          onChange={(e) => setSelectedActType(e.target.value)}
                          style={{ height: 32, fontSize: 12, padding: "4px 8px" }}
                          data-testid="filter-activity-select"
                        >
                          <option value="All">All Activities</option>
                          <option value="call">Calls Only</option>
                          <option value="lead">Leads</option>
                          <option value="order">Orders</option>
                          <option value="delivery">Deliveries</option>
                          <option value="task">Tasks</option>
                          <option value="followup">Follow-ups</option>
                        </select>
                      </div>

                      <div>
                        <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", marginBottom: 4 }}>
                          Report Status
                        </label>
                        <select
                          className="form-control"
                          value={selectedReportStatus}
                          onChange={(e) => setSelectedReportStatus(e.target.value)}
                          style={{ height: 32, fontSize: 12, padding: "4px 8px" }}
                          data-testid="filter-report-status-select"
                        >
                          <option value="All">All Statuses</option>
                          <option value="submitted">Submitted</option>
                          <option value="not_submitted">Not Submitted</option>
                        </select>
                      </div>

                      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
                        <button
                          type="button"
                          className="primary-btn compact"
                          style={{ fontSize: 11, padding: "5px 12px" }}
                          onClick={() => setShowMoreFilters(false)}
                        >
                          Done
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Reset Option */}
              {hasAnyFilterActive && (
                <button
                  type="button"
                  onClick={handleResetFilters}
                  style={{
                    background: "none",
                    border: "none",
                    color: "var(--muted)",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    padding: "4px 8px",
                    textDecoration: "underline",
                  }}
                >
                  Reset Filters
                </button>
              )}
            </div>
          </div>

          {/* 4. Primary Navigation Tabs (4 Tabs) + Secondary Insights Toggle */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid var(--line)",
              marginBottom: 20,
              flexWrap: "wrap",
              gap: 8,
            }}
          >
            <div className="tabs" style={{ margin: 0, border: "none" }}>
              <button
                className={`tab ${activeTab === "overview" ? "active" : ""}`}
                onClick={() => setActiveTab("overview")}
                data-testid="tab-user-wise"
              >
                Team Overview
              </button>

              <button
                className={`tab ${activeTab === "activity" ? "active" : ""}`}
                onClick={() => setActiveTab("activity")}
                data-testid="tab-timeline"
              >
                Today's Activity
              </button>

              <button
                className={`tab ${activeTab === "reports" ? "active" : ""}`}
                onClick={() => setActiveTab("reports")}
                data-testid="tab-daily-reports"
              >
                Daily Reports
              </button>

              <button
                className={`tab ${activeTab === "departments" ? "active" : ""}`}
                onClick={() => setActiveTab("departments")}
                data-testid="tab-departments"
              >
                Department Summary
              </button>
            </div>

            {/* Secondary View: Revenue & Pipeline Insights */}
            <button
              type="button"
              onClick={() => setActiveTab(activeTab === "insights" ? "overview" : "insights")}
              style={{
                background: activeTab === "insights" ? "#eff6ff" : "transparent",
                border: "1px solid",
                borderColor: activeTab === "insights" ? "#bfdbfe" : "var(--line)",
                color: activeTab === "insights" ? "var(--blue)" : "var(--muted)",
                borderRadius: 6,
                padding: "6px 12px",
                fontSize: 12,
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                cursor: "pointer",
                marginBottom: 6,
              }}
              data-testid="tab-insights"
            >
              <TrendingUp size={13} />
              {activeTab === "insights" ? "Hide Business Insights" : "Revenue & Pipeline Insights"}
            </button>
          </div>

          {/* TAB 1: Team Overview Table (Clean, Compact, No Horizontal Scroll) */}
          {activeTab === "overview" && (
            <section className="panel" style={{ padding: 0, overflow: "hidden" }}>
              {/* Header Bar with Simple Search Field */}
              <div
                style={{
                  padding: "12px 18px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  background: "#f8fafc",
                  borderBottom: "1px solid var(--line)",
                  flexWrap: "wrap",
                  gap: 10,
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--navy)" }}>
                  Team Overview ({filteredUsers.length})
                </div>

                <div className="search-box" style={{ width: 230 }}>
                  <Search size={14} style={{ color: "var(--muted)" }} />
                  <input
                    placeholder="Search employee…"
                    value={userSearch}
                    onChange={(e) => {
                      setUserSearch(e.target.value);
                      setCurrentPage(1);
                    }}
                    data-testid="user-table-search-input"
                  />
                </div>
              </div>

              {/* Department-Adaptive Table: Adapts columns to Sales, Delivery, Design, Production, Procurement, or General */}
              <table className="panel-table" style={{ minWidth: "100%", width: "100%", tableLayout: "auto" }}>
                <thead>
                  {selectedDept === "Sales" ? (
                    <tr>
                      <th onClick={() => handleSort("name")} style={{ cursor: "pointer", width: "24%" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          Employee <ArrowUpDown size={11} style={{ opacity: 0.6 }} />
                        </div>
                      </th>
                      <th onClick={() => handleSort("department")} style={{ cursor: "pointer", width: "12%" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          Department <ArrowUpDown size={11} style={{ opacity: 0.6 }} />
                        </div>
                      </th>
                      <th onClick={() => handleSort("calls_made")} style={{ cursor: "pointer", textAlign: "center", width: "8%" }}>
                        Calls (Today)
                      </th>
                      <th onClick={() => handleSort("leads_won")} style={{ cursor: "pointer", textAlign: "center", width: "9%" }}>
                        Won (Today)
                      </th>
                      <th onClick={() => handleSort("leads_qualified")} style={{ cursor: "pointer", textAlign: "center", width: "9%" }}>
                        Qualified (Today)
                      </th>
                      <th onClick={() => handleSort("leads_created")} style={{ cursor: "pointer", textAlign: "center", width: "9%" }}>
                        Created (Today)
                      </th>
                      <th style={{ width: "18%" }}>
                        Current Pipeline
                      </th>
                      <th style={{ textAlign: "center", width: "11%" }}>Report Status</th>
                      <th style={{ textAlign: "right", width: "8%" }}>Action</th>
                    </tr>
                  ) : selectedDept === "Delivery" ? (
                    <tr>
                      <th onClick={() => handleSort("name")} style={{ cursor: "pointer", width: "25%" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          Employee <ArrowUpDown size={11} style={{ opacity: 0.6 }} />
                        </div>
                      </th>
                      <th onClick={() => handleSort("department")} style={{ cursor: "pointer", width: "14%" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          Department <ArrowUpDown size={11} style={{ opacity: 0.6 }} />
                        </div>
                      </th>
                      <th onClick={() => handleSort("deliveries_completed")} style={{ cursor: "pointer", textAlign: "center", width: "12%" }}>
                        Delivered (Today)
                      </th>
                      <th style={{ textAlign: "center", width: "13%" }}>
                        Assigned Deliveries
                      </th>
                      <th style={{ textAlign: "center", width: "12%" }}>
                        Pending Deliveries
                      </th>
                      <th style={{ textAlign: "center", width: "12%" }}>
                        Challans (Today)
                      </th>
                      <th style={{ textAlign: "center", width: "11%" }}>Report Status</th>
                      <th style={{ textAlign: "right", width: "8%" }}>Action</th>
                    </tr>
                  ) : selectedDept === "Design" ? (
                    <tr>
                      <th onClick={() => handleSort("name")} style={{ cursor: "pointer", width: "25%" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          Employee <ArrowUpDown size={11} style={{ opacity: 0.6 }} />
                        </div>
                      </th>
                      <th onClick={() => handleSort("department")} style={{ cursor: "pointer", width: "14%" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          Department <ArrowUpDown size={11} style={{ opacity: 0.6 }} />
                        </div>
                      </th>
                      <th style={{ textAlign: "center", width: "14%" }}>
                        Designs Done (Today)
                      </th>
                      <th style={{ textAlign: "center", width: "14%" }}>
                        Assigned Designs
                      </th>
                      <th style={{ textAlign: "center", width: "14%" }}>
                        Awaiting Approval
                      </th>
                      <th style={{ textAlign: "center", width: "14%" }}>
                        Approved (Today)
                      </th>
                      <th style={{ textAlign: "center", width: "11%" }}>Report Status</th>
                      <th style={{ textAlign: "right", width: "8%" }}>Action</th>
                    </tr>
                  ) : selectedDept === "Production" ? (
                    <tr>
                      <th onClick={() => handleSort("name")} style={{ cursor: "pointer", width: "25%" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          Employee <ArrowUpDown size={11} style={{ opacity: 0.6 }} />
                        </div>
                      </th>
                      <th onClick={() => handleSort("department")} style={{ cursor: "pointer", width: "14%" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          Department <ArrowUpDown size={11} style={{ opacity: 0.6 }} />
                        </div>
                      </th>
                      <th style={{ textAlign: "center", width: "14%" }}>
                        Finished (Today)
                      </th>
                      <th style={{ textAlign: "center", width: "14%" }}>
                        In Production
                      </th>
                      <th style={{ textAlign: "center", width: "14%" }}>
                        Entering Prod
                      </th>
                      <th style={{ textAlign: "center", width: "14%" }}>
                        Overdue Work
                      </th>
                      <th style={{ textAlign: "center", width: "11%" }}>Report Status</th>
                      <th style={{ textAlign: "right", width: "8%" }}>Action</th>
                    </tr>
                  ) : selectedDept === "Procurement" ? (
                    <tr>
                      <th onClick={() => handleSort("name")} style={{ cursor: "pointer", width: "25%" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          Employee <ArrowUpDown size={11} style={{ opacity: 0.6 }} />
                        </div>
                      </th>
                      <th onClick={() => handleSort("department")} style={{ cursor: "pointer", width: "14%" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          Department <ArrowUpDown size={11} style={{ opacity: 0.6 }} />
                        </div>
                      </th>
                      <th style={{ textAlign: "center", width: "14%" }}>
                        Priced (Today)
                      </th>
                      <th style={{ textAlign: "center", width: "14%" }}>
                        Assigned Requests
                      </th>
                      <th style={{ textAlign: "center", width: "14%" }}>
                        Pending Requests
                      </th>
                      <th style={{ textAlign: "center", width: "14%" }}>
                        Overdue Requests
                      </th>
                      <th style={{ textAlign: "center", width: "11%" }}>Report Status</th>
                      <th style={{ textAlign: "right", width: "8%" }}>Action</th>
                    </tr>
                  ) : (
                    <tr>
                      <th onClick={() => handleSort("name")} style={{ cursor: "pointer", width: "23%" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          Employee <ArrowUpDown size={11} style={{ opacity: 0.6 }} />
                        </div>
                      </th>
                      <th onClick={() => handleSort("department")} style={{ cursor: "pointer", width: "13%" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          Department <ArrowUpDown size={11} style={{ opacity: 0.6 }} />
                        </div>
                      </th>
                      <th onClick={() => handleSort("calls_made")} style={{ cursor: "pointer", textAlign: "center", width: "8%" }}>
                        Calls
                      </th>
                      <th onClick={() => handleSort("leads_qualified")} style={{ cursor: "pointer", textAlign: "center", width: "10%" }}>
                        Leads Qualified
                      </th>
                      <th onClick={() => handleSort("deliveries_completed")} style={{ cursor: "pointer", textAlign: "center", width: "10%" }}>
                        Orders Delivered
                      </th>
                      <th onClick={() => handleSort("tasks_completed")} style={{ cursor: "pointer", textAlign: "center", width: "10%" }}>
                        Tasks Completed
                      </th>
                      <th style={{ width: "18%" }}>
                        Current Pipeline / Queue
                      </th>
                      <th style={{ textAlign: "center", width: "9%" }}>Report Status</th>
                      <th style={{ textAlign: "right", width: "7%" }}>Action</th>
                    </tr>
                  )}
                </thead>
                <tbody>
                  {paginatedUsers.length === 0 ? (
                    <tr>
                      <td colSpan={9} style={{ textAlign: "center", padding: "36px 12px", color: "var(--muted)" }}>
                        No team members match the search query or filters.
                      </td>
                    </tr>
                  ) : (
                    paginatedUsers.map((u) => {
                      const isSubmitted = u.daily_report_status === "Submitted";
                      const rm = u.role_metrics || {};
                      const pipe = u.pipeline || {};
                      const userDepts = (u.departments || [u.department || ""]).map((d) => (d || "").toLowerCase());

                      return (
                        <tr key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                          {/* Employee Name & Email */}
                          <td>
                            <div
                              style={{ fontWeight: 600, color: "var(--navy)", fontSize: 13, cursor: "pointer" }}
                              onClick={() => setViewReportUser(u)}
                              title="Click to view full details"
                            >
                              {u.name}
                            </div>
                            <div style={{ fontSize: 11, color: "var(--muted)" }}>{u.email}</div>
                          </td>

                          {/* Department */}
                          <td>
                            <span className="badge blue">
                              {u.department || "General"}
                            </span>
                          </td>

                          {/* Department-Specific Data Cells */}
                          {selectedDept === "Sales" ? (
                            <>
                              <td style={{ textAlign: "center", fontWeight: u.calls_made > 0 ? 700 : 400, color: u.calls_made > 0 ? "var(--navy)" : "var(--muted)" }}>
                                {u.calls_made}
                              </td>
                              <td style={{ textAlign: "center", fontWeight: (u.leads_won || u.leads_converted) > 0 ? 700 : 400, color: (u.leads_won || u.leads_converted) > 0 ? "var(--green)" : "var(--muted)" }}>
                                {u.leads_won ?? u.leads_converted ?? 0}
                              </td>
                              <td style={{ textAlign: "center", fontWeight: u.leads_qualified > 0 ? 700 : 400, color: u.leads_qualified > 0 ? "var(--green)" : "var(--muted)" }}>
                                {u.leads_qualified}
                              </td>
                              <td style={{ textAlign: "center", fontWeight: (u.leads_created || u.new_leads) > 0 ? 700 : 400, color: (u.leads_created || u.new_leads) > 0 ? "var(--navy)" : "var(--muted)" }}>
                                {u.leads_created ?? u.new_leads ?? 0}
                              </td>
                              <td>
                                <div style={{ fontSize: 12 }}>
                                  <strong style={{ color: "var(--navy)" }}>{pipe.total_leads || 0} leads</strong>
                                  {pipe.won > 0 && (
                                    <span className="badge green" style={{ marginLeft: 6, fontSize: 10.5 }}>
                                      {pipe.won} Won ({compactMoney(pipe.won_value || 0)})
                                    </span>
                                  )}
                                  {pipe.qualified > 0 && (
                                    <span className="badge amber" style={{ marginLeft: 4, fontSize: 10.5 }}>
                                      {pipe.qualified} Qual
                                    </span>
                                  )}
                                </div>
                              </td>
                            </>
                          ) : selectedDept === "Delivery" ? (
                            <>
                              <td style={{ textAlign: "center", fontWeight: u.deliveries_completed > 0 ? 700 : 400, color: u.deliveries_completed > 0 ? "var(--green)" : "var(--muted)" }}>
                                {u.deliveries_completed}
                              </td>
                              <td style={{ textAlign: "center" }}>
                                {rm.orders_assigned_for_delivery ?? 0}
                              </td>
                              <td style={{ textAlign: "center", fontWeight: (rm.deliveries_pending || 0) > 0 ? 700 : 400, color: (rm.deliveries_pending || 0) > 0 ? "var(--amber)" : "var(--muted)" }}>
                                {rm.deliveries_pending ?? 0}
                              </td>
                              <td style={{ textAlign: "center" }}>
                                {rm.challans_completed ?? 0}
                              </td>
                            </>
                          ) : selectedDept === "Design" ? (
                            <>
                              <td style={{ textAlign: "center", fontWeight: (rm.designs_completed || 0) > 0 ? 700 : 400, color: (rm.designs_completed || 0) > 0 ? "var(--green)" : "var(--muted)" }}>
                                {rm.designs_completed ?? 0}
                              </td>
                              <td style={{ textAlign: "center" }}>
                                {rm.design_tasks_assigned ?? 0}
                              </td>
                              <td style={{ textAlign: "center", fontWeight: (rm.designs_awaiting_approval || 0) > 0 ? 700 : 400, color: (rm.designs_awaiting_approval || 0) > 0 ? "var(--amber)" : "var(--muted)" }}>
                                {rm.designs_awaiting_approval ?? 0}
                              </td>
                              <td style={{ textAlign: "center" }}>
                                {rm.designs_approved_today ?? 0}
                              </td>
                            </>
                          ) : selectedDept === "Production" ? (
                            <>
                              <td style={{ textAlign: "center", fontWeight: (rm.production_tasks_completed || 0) > 0 ? 700 : 400, color: (rm.production_tasks_completed || 0) > 0 ? "var(--green)" : "var(--muted)" }}>
                                {rm.production_tasks_completed ?? 0}
                              </td>
                              <td style={{ textAlign: "center" }}>
                                {rm.orders_pending_production ?? 0}
                              </td>
                              <td style={{ textAlign: "center" }}>
                                {rm.orders_entering_production ?? 0}
                              </td>
                              <td style={{ textAlign: "center", color: (rm.overdue_production_work || 0) > 0 ? "var(--red)" : "var(--muted)" }}>
                                {rm.overdue_production_work ?? 0}
                              </td>
                            </>
                          ) : selectedDept === "Procurement" ? (
                            <>
                              <td style={{ textAlign: "center", fontWeight: (rm.price_requests_completed || 0) > 0 ? 700 : 400, color: (rm.price_requests_completed || 0) > 0 ? "var(--green)" : "var(--muted)" }}>
                                {rm.price_requests_completed ?? 0}
                              </td>
                              <td style={{ textAlign: "center" }}>
                                {rm.price_requests_assigned ?? 0}
                              </td>
                              <td style={{ textAlign: "center", fontWeight: (rm.price_requests_pending || 0) > 0 ? 700 : 400, color: (rm.price_requests_pending || 0) > 0 ? "var(--amber)" : "var(--muted)" }}>
                                {rm.price_requests_pending ?? 0}
                              </td>
                              <td style={{ textAlign: "center", color: (rm.price_requests_overdue || 0) > 0 ? "var(--red)" : "var(--muted)" }}>
                                {rm.price_requests_overdue ?? 0}
                              </td>
                            </>
                          ) : (
                            <>
                              <td style={{ textAlign: "center", fontWeight: u.calls_made > 0 ? 700 : 400, color: u.calls_made > 0 ? "var(--navy)" : "var(--muted)" }}>
                                {u.calls_made}
                              </td>
                              <td style={{ textAlign: "center", fontWeight: u.leads_qualified > 0 ? 700 : 400, color: u.leads_qualified > 0 ? "var(--green)" : "var(--muted)" }}>
                                {u.leads_qualified}
                              </td>
                              <td style={{ textAlign: "center", fontWeight: u.deliveries_completed > 0 ? 700 : 400, color: u.deliveries_completed > 0 ? "var(--green)" : "var(--muted)" }}>
                                {u.deliveries_completed}
                              </td>
                              <td style={{ textAlign: "center", fontWeight: u.tasks_completed > 0 ? 700 : 400, color: u.tasks_completed > 0 ? "var(--navy)" : "var(--muted)" }}>
                                {u.tasks_completed}
                              </td>
                              <td>
                                {userDepts.includes("sales") ? (
                                  <div style={{ fontSize: 12 }}>
                                    <strong style={{ color: "var(--navy)" }}>{pipe.total_leads || 0} leads</strong>
                                    {pipe.won > 0 && (
                                      <span className="badge green" style={{ marginLeft: 6, fontSize: 10.5 }}>
                                        {pipe.won} Won
                                      </span>
                                    )}
                                  </div>
                                ) : userDepts.includes("delivery") ? (
                                  <div style={{ fontSize: 12, color: "var(--navy)" }}>
                                    {rm.deliveries_pending || 0} pending deliveries
                                  </div>
                                ) : userDepts.includes("design") ? (
                                  <div style={{ fontSize: 12, color: "var(--navy)" }}>
                                    {rm.designs_awaiting_approval || 0} awaiting approval
                                  </div>
                                ) : userDepts.includes("production") ? (
                                  <div style={{ fontSize: 12, color: "var(--navy)" }}>
                                    {rm.orders_pending_production || 0} in production
                                  </div>
                                ) : userDepts.includes("procurement") ? (
                                  <div style={{ fontSize: 12, color: "var(--navy)" }}>
                                    {rm.price_requests_pending || 0} pending quotes
                                  </div>
                                ) : (
                                  <div style={{ fontSize: 12, color: "var(--muted)" }}>
                                    {u.tasks?.length || 0} tasks
                                  </div>
                                )}
                              </td>
                            </>
                          )}

                          {/* Report Status */}
                          <td style={{ textAlign: "center" }}>
                            {isSubmitted ? (
                              <span className="badge green">
                                ✓ Submitted
                              </span>
                            ) : (
                              <span className="badge amber">
                                Pending
                              </span>
                            )}
                          </td>

                          {/* Action: View Details */}
                          <td style={{ textAlign: "right" }}>
                            <button
                              className="outline-btn"
                              onClick={() => setViewReportUser(u)}
                              style={{
                                padding: "4px 10px",
                                fontSize: 11.5,
                                whiteSpace: "nowrap",
                              }}
                              data-testid={`btn-view-report-${u.user_id}`}
                            >
                              <Eye size={12} /> View Details
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>

              {filteredUsers.length > 0 && (
                <div style={{ padding: "8px 16px", borderTop: "1px solid #f1f5f9" }}>
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    pageSize={pageSize}
                    onPageSizeChange={setPageSize}
                    totalItems={filteredUsers.length}
                  />
                </div>
              )}
            </section>
          )}

          {/* TAB 2: Today's Activity (Straightforward chronological list) */}
          {activeTab === "activity" && (
            <section className="panel" style={{ padding: 0, overflow: "hidden" }}>
              {/* Header with Search */}
              <div
                style={{
                  padding: "12px 18px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  background: "#f8fafc",
                  borderBottom: "1px solid var(--line)",
                  flexWrap: "wrap",
                  gap: 10,
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--navy)" }}>
                  Today's Activity ({filteredActivityList.length})
                </div>

                <div className="search-box" style={{ width: 230 }}>
                  <Search size={14} style={{ color: "var(--muted)" }} />
                  <input
                    placeholder="Filter activity…"
                    value={activitySearch}
                    onChange={(e) => setActivitySearch(e.target.value)}
                  />
                </div>
              </div>

              {filteredActivityList.length === 0 ? (
                <div style={{ padding: "40px 16px", textAlign: "center", color: "var(--muted)", fontSize: 13 }}>
                  No activity events recorded for the selected date and filters.
                </div>
              ) : (
                <table className="panel-table" style={{ minWidth: "100%", width: "100%" }}>
                  <thead>
                    <tr>
                      <th style={{ width: "12%" }}>Time</th>
                      <th style={{ width: "22%" }}>Employee</th>
                      <th style={{ width: "36%" }}>Activity</th>
                      <th style={{ width: "18%" }}>Related Item</th>
                      <th style={{ width: "12%", textAlign: "right" }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredActivityList.map((act, idx) => (
                      <tr key={idx}>
                        <td style={{ color: "var(--muted)", fontSize: 12 }}>
                          {act.formatted_time || "—"}
                        </td>
                        <td>
                          <div style={{ fontWeight: 600, color: "var(--navy)" }}>{act.user_name}</div>
                          <div style={{ fontSize: 11, color: "var(--muted)" }}>{act.user_department}</div>
                        </td>
                        <td style={{ color: "#1e293b" }}>
                          <div>{act.action}</div>
                          {act.remarks && (
                            <div style={{ fontSize: 11, color: "var(--muted)", fontStyle: "italic", marginTop: 2 }}>
                              "{act.remarks}"
                            </div>
                          )}
                        </td>
                        <td>
                          {act.entity_name ? (
                            <span style={{ color: "#334155" }}>
                              {act.entity_name} {act.entity_id ? `(${act.entity_id})` : ""}
                            </span>
                          ) : (
                            <span style={{ color: "#94a3b8" }}>—</span>
                          )}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {act.status_change || act.outcome ? (
                            <span className="badge green">
                              {act.status_change || act.outcome}
                            </span>
                          ) : (
                            <span className="badge blue">{act.type}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          )}

          {/* TAB 3: Daily Reports (Employee, Dept, Status, Submission time, Short summary, Action) */}
          {activeTab === "reports" && (
            <section className="panel" style={{ padding: 0, overflow: "hidden" }}>
              {/* Header with Status Filter Filter */}
              <div
                style={{
                  padding: "12px 18px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  background: "#f8fafc",
                  borderBottom: "1px solid var(--line)",
                  flexWrap: "wrap",
                  gap: 10,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--navy)" }}>
                    Daily Reports ({filteredDailyReportsList.length})
                  </div>
                  <div style={{ display: "flex", gap: 4 }}>
                    {["All", "Submitted", "Pending"].map((st) => (
                      <button
                        key={st}
                        type="button"
                        onClick={() => setReportStatusFilter(st)}
                        style={{
                          background: reportStatusFilter === st ? "var(--blue)" : "#f1f5f9",
                          color: reportStatusFilter === st ? "#ffffff" : "#475569",
                          border: "none",
                          borderRadius: 4,
                          padding: "3px 10px",
                          fontSize: 11.5,
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        {st}
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{ fontSize: 12, color: "var(--muted)" }}>
                  Submitted: <strong style={{ color: "var(--green)" }}>{kpis.daily_reports_submitted ?? 0}</strong> / {(teamData?.users || []).length}
                </div>
              </div>

              {filteredDailyReportsList.length === 0 ? (
                <div style={{ padding: "40px 16px", textAlign: "center", color: "var(--muted)", fontSize: 13 }}>
                  No daily reports match the current filter.
                </div>
              ) : (
                <table className="panel-table" style={{ minWidth: "100%", width: "100%" }}>
                  <thead>
                    <tr>
                      <th style={{ width: "22%" }}>Employee</th>
                      <th style={{ width: "14%" }}>Department</th>
                      <th style={{ width: "12%", textAlign: "center" }}>Report Status</th>
                      <th style={{ width: "14%" }}>Submission Time</th>
                      <th style={{ width: "28%" }}>Work Summary</th>
                      <th style={{ width: "10%", textAlign: "right" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDailyReportsList.map((u) => {
                      const isSubmitted = u.daily_report_status === "Submitted";
                      const rep = u.daily_report;
                      return (
                        <tr key={u.user_id}>
                          <td>
                            <div style={{ fontWeight: 600, color: "var(--navy)" }}>{u.name}</div>
                            <div style={{ fontSize: 11, color: "var(--muted)" }}>{u.email}</div>
                          </td>
                          <td>
                            <span className="badge blue">{u.department}</span>
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {isSubmitted ? (
                              <span className="badge green">
                                ✓ Submitted
                              </span>
                            ) : (
                              <span className="badge amber">
                                Pending
                              </span>
                            )}
                          </td>
                          <td style={{ fontSize: 12, color: "var(--muted)" }}>
                            {rep?.submitted_at ? formatDateDisplay(rep.submitted_at.split("T")[0]) : "—"}
                          </td>
                          <td style={{ color: "#334155" }}>
                            {rep?.work_summary ? (
                              <div
                                style={{
                                  maxWidth: 280,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                                title={rep.work_summary}
                              >
                                {rep.work_summary}
                              </div>
                            ) : (
                              <span style={{ color: "#94a3b8", fontStyle: "italic" }}>No summary provided</span>
                            )}
                          </td>
                          <td style={{ textAlign: "right" }}>
                            <button
                              className="outline-btn"
                              onClick={() => setViewReportUser(u)}
                              style={{ padding: "4px 10px", fontSize: 11.5 }}
                            >
                              <Eye size={12} /> View Report
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </section>
          )}

          {/* TAB 4: Department Summary (Relevant metrics per department) */}
          {activeTab === "departments" && (
            <section style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                  gap: 14,
                }}
              >
                {(teamData?.department_summary || []).map((dept) => {
                  const dName = (dept.department || "").toLowerCase();
                  const isSelected = selectedDeptCard === dept.department;

                  return (
                    <div
                      key={dept.department}
                      className="panel"
                      onClick={() => setSelectedDeptCard(isSelected ? "All" : dept.department)}
                      style={{
                        padding: "16px 18px",
                        cursor: "pointer",
                        border: isSelected ? "2px solid var(--blue)" : "1px solid var(--line)",
                        transition: "all 0.15s ease",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                        <strong style={{ fontSize: 15, color: "var(--navy)" }}>{dept.department}</strong>
                        <span style={{ fontSize: 11.5, color: "var(--muted)" }}>
                          {dept.team_size} member{dept.team_size === 1 ? "" : "s"}
                        </span>
                      </div>

                      {/* Relevant department specific metrics */}
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 12.5, color: "#334155" }}>
                        {dName === "sales" && (
                          <>
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                              <span style={{ color: "var(--muted)" }}>Calls Logged:</span>
                              <strong>{dept.calls_made}</strong>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                              <span style={{ color: "var(--muted)" }}>Orders Processed:</span>
                              <strong>{dept.orders_processed}</strong>
                            </div>
                          </>
                        )}

                        {dName === "delivery" && (
                          <>
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                              <span style={{ color: "var(--muted)" }}>Deliveries Done:</span>
                              <strong style={{ color: "var(--green)" }}>{dept.deliveries_completed}</strong>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                              <span style={{ color: "var(--muted)" }}>Tasks Completed:</span>
                              <strong>{dept.tasks_completed}</strong>
                            </div>
                          </>
                        )}

                        {dName === "production" && (
                          <>
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                              <span style={{ color: "var(--muted)" }}>Batches / Orders:</span>
                              <strong>{dept.orders_processed}</strong>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                              <span style={{ color: "var(--muted)" }}>Tasks Completed:</span>
                              <strong>{dept.tasks_completed}</strong>
                            </div>
                          </>
                        )}

                        {dName === "design" && (
                          <>
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                              <span style={{ color: "var(--muted)" }}>Tasks Completed:</span>
                              <strong>{dept.tasks_completed}</strong>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                              <span style={{ color: "var(--muted)" }}>Orders Touched:</span>
                              <strong>{dept.orders_processed}</strong>
                            </div>
                          </>
                        )}

                        {dName !== "sales" && dName !== "delivery" && dName !== "production" && dName !== "design" && (
                          <>
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                              <span style={{ color: "var(--muted)" }}>Tasks Completed:</span>
                              <strong>{dept.tasks_completed}</strong>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                              <span style={{ color: "var(--muted)" }}>Reports Submitted:</span>
                              <strong>{dept.reports_submitted} / {dept.team_size}</strong>
                            </div>
                          </>
                        )}

                        <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid #f1f5f9", paddingTop: 6, marginTop: 4 }}>
                          <span style={{ color: "var(--muted)" }}>Daily Reports:</span>
                          <span style={{ color: dept.reports_submitted === dept.team_size ? "var(--green)" : "var(--amber)", fontWeight: 700 }}>
                            {dept.reports_submitted} / {dept.team_size}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Members in selected department breakdown */}
              {selectedDeptCard !== "All" && (
                <div className="panel" style={{ padding: 18 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "var(--navy)" }}>
                      {selectedDeptCard} Team Members
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedDeptCard("All")}
                      style={{ background: "none", border: "none", color: "var(--muted)", fontSize: 11.5, cursor: "pointer", textDecoration: "underline" }}
                    >
                      Clear Selection
                    </button>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 10 }}>
                    {(teamData?.users || [])
                      .filter((u) => u.department === selectedDeptCard)
                      .map((u) => (
                        <div
                          key={u.user_id}
                          style={{
                            padding: "12px 14px",
                            background: "#f8fafc",
                            border: "1px solid var(--line)",
                            borderRadius: 6,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13, color: "var(--navy)" }}>{u.name}</div>
                            <div style={{ fontSize: 11, color: "var(--muted)" }}>
                              {u.daily_report_status === "Submitted" ? "✓ Report Submitted" : "Pending Report"}
                            </div>
                          </div>
                          <button
                            className="outline-btn"
                            onClick={() => setViewReportUser(u)}
                            style={{ padding: "3px 8px", fontSize: 11 }}
                          >
                            Details
                          </button>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Secondary View: Revenue & Pipeline Insights */}
          {activeTab === "insights" && generalReports && (
            <section style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div className="metrics-grid">
                <div className="metric">
                  <div className="metric-top"><span>ORDER VOLUME</span></div>
                  <strong>{generalReports.order_volume}</strong>
                </div>
                <div className="metric green">
                  <div className="metric-top"><span>REVENUE BOOKED</span></div>
                  <strong>{compactMoney(generalReports.revenue)}</strong>
                </div>
                <div className="metric red">
                  <div className="metric-top"><span>OUTSTANDING</span></div>
                  <strong>{compactMoney(generalReports.outstanding_total)}</strong>
                </div>
                <div className="metric amber">
                  <div className="metric-top"><span>AVG ORDER → DELIVERY</span></div>
                  <strong>{generalReports.avg_order_to_delivery}d</strong>
                </div>
              </div>

              <div className="metrics-grid">
                <div className="metric amber">
                  <div className="metric-top"><span>PENDING APPROVALS</span></div>
                  <strong>{generalReports.pending_approvals}</strong>
                </div>
                <div className="metric">
                  <div className="metric-top"><span>PRODUCTION PENDING</span></div>
                  <strong>{generalReports.production_pending}</strong>
                </div>
                <div className="metric">
                  <div className="metric-top"><span>DISPATCH PENDING</span></div>
                  <strong>{generalReports.dispatch_pending}</strong>
                </div>
                <div className="metric">
                  <div className="metric-top"><span>DELIVERY PENDING</span></div>
                  <strong>{generalReports.delivery_pending}</strong>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16 }}>
                <div className="panel" style={{ padding: 18 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "var(--navy)", marginBottom: 14 }}>
                    Top Clients by Revenue
                  </div>
                  <div style={{ width: "100%", height: 250 }} data-testid="report-clients-chart">
                    <ResponsiveContainer>
                      <BarChart data={generalReports.by_client} layout="vertical" margin={{ left: 10 }}>
                        <CartesianGrid stroke="#f1f5f9" horizontal={false} />
                        <XAxis type="number" stroke="var(--muted)" fontSize={11} />
                        <YAxis type="category" dataKey="client" stroke="var(--muted)" fontSize={11} width={110} />
                        <Tooltip formatter={(v) => money(v)} />
                        <Bar dataKey="revenue" fill="var(--blue)" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="panel" style={{ padding: 18 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "var(--navy)", marginBottom: 14 }}>
                    Top Products by Revenue
                  </div>
                  <div style={{ width: "100%", height: 250 }} data-testid="report-products-chart">
                    <ResponsiveContainer>
                      <BarChart data={generalReports.by_product} layout="vertical" margin={{ left: 10 }}>
                        <CartesianGrid stroke="#f1f5f9" horizontal={false} />
                        <XAxis type="number" stroke="var(--muted)" fontSize={11} />
                        <YAxis type="category" dataKey="product" stroke="var(--muted)" fontSize={11} width={120} />
                        <Tooltip formatter={(v) => money(v)} />
                        <Bar dataKey="revenue" fill="var(--green)" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </section>
          )}
        </>
      )}

      {/* Non-Admin View (General Reports) */}
      {!canAccessTeamReports && generalReports && (
        <section style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div className="metrics-grid">
            <div className="metric">
              <div className="metric-top"><span>ORDER VOLUME</span></div>
              <strong>{generalReports.order_volume}</strong>
            </div>
            <div className="metric green">
              <div className="metric-top"><span>REVENUE BOOKED</span></div>
              <strong>{compactMoney(generalReports.revenue)}</strong>
            </div>
            <div className="metric red">
              <div className="metric-top"><span>OUTSTANDING</span></div>
              <strong>{compactMoney(generalReports.outstanding_total)}</strong>
            </div>
            <div className="metric amber">
              <div className="metric-top"><span>AVG ORDER → DELIVERY</span></div>
              <strong>{generalReports.avg_order_to_delivery}d</strong>
            </div>
          </div>

          <div className="metrics-grid">
            <div className="metric amber">
              <div className="metric-top"><span>PENDING APPROVALS</span></div>
              <strong>{generalReports.pending_approvals}</strong>
            </div>
            <div className="metric">
              <div className="metric-top"><span>PRODUCTION PENDING</span></div>
              <strong>{generalReports.production_pending}</strong>
            </div>
            <div className="metric">
              <div className="metric-top"><span>DISPATCH PENDING</span></div>
              <strong>{generalReports.dispatch_pending}</strong>
            </div>
            <div className="metric">
              <div className="metric-top"><span>DELIVERY PENDING</span></div>
              <strong>{generalReports.delivery_pending}</strong>
            </div>
          </div>
        </section>
      )}

      {/* MODAL: Dedicated Employee Detail Modal */}
      {viewReportUser && (
        <IndividualDailyReportModal
          user={viewReportUser}
          reportDate={reportDate}
          onClose={() => setViewReportUser(null)}
        />
      )}

      {/* MODAL: Submit Daily Report */}
      {showSubmitModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(15, 23, 42, 0.6)",
            zIndex: 1000,
            display: "grid",
            placeItems: "center",
            padding: 16,
          }}
        >
          <div
            style={{
              background: "#ffffff",
              borderRadius: 8,
              width: "100%",
              maxWidth: 540,
              maxHeight: "90vh",
              overflowY: "auto",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2)",
              border: "1px solid var(--line)",
            }}
          >
            <div
              style={{
                padding: "16px 20px",
                borderBottom: "1px solid var(--line)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "#f8fafc",
              }}
            >
              <div>
                <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: "var(--navy)" }}>
                  Submit Daily Work Report
                </h2>
                <span style={{ fontSize: 12, color: "var(--muted)" }}>
                  Date: <strong>{formatDateDisplay(reportDate)}</strong>
                </span>
              </div>
              <button
                onClick={() => setShowSubmitModal(false)}
                style={{ background: "none", border: "none", color: "var(--muted)", cursor: "pointer", padding: 4 }}
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSaveDailyReport} style={{ padding: "18px 20px" }}>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#1e293b", marginBottom: 5 }}>
                  Work Summary for Today <span style={{ color: "var(--red)" }}>*</span>
                </label>
                <textarea
                  className="form-control"
                  rows={3}
                  required
                  placeholder="Summary of accomplishments, tasks, client communications..."
                  value={myReportForm.work_summary}
                  onChange={(e) => setMyReportForm({ ...myReportForm, work_summary: e.target.value })}
                  data-testid="input-work-summary"
                  style={{ width: "100%", fontSize: 13 }}
                />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#1e293b", marginBottom: 5 }}>
                  Tasks Completed
                </label>
                <textarea
                  className="form-control"
                  rows={2}
                  placeholder="Key deliverables finished today..."
                  value={myReportForm.tasks_completed_summary}
                  onChange={(e) => setMyReportForm({ ...myReportForm, tasks_completed_summary: e.target.value })}
                  style={{ width: "100%", fontSize: 13 }}
                />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#1e293b", marginBottom: 5 }}>
                  Work Still Pending / Agenda
                </label>
                <textarea
                  className="form-control"
                  rows={2}
                  placeholder="Pending tasks, follow-ups scheduled for next day..."
                  value={myReportForm.tasks_pending_summary}
                  onChange={(e) => setMyReportForm({ ...myReportForm, tasks_pending_summary: e.target.value })}
                  style={{ width: "100%", fontSize: 13 }}
                />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#1e293b", marginBottom: 5 }}>
                  Blockers or Issues (if any)
                </label>
                <textarea
                  className="form-control"
                  rows={2}
                  placeholder="Bottlenecks or external dependencies..."
                  value={myReportForm.blockers}
                  onChange={(e) => setMyReportForm({ ...myReportForm, blockers: e.target.value })}
                  style={{ width: "100%", fontSize: 13 }}
                />
              </div>

              <div style={{ marginBottom: 18 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#1e293b", marginBottom: 5 }}>
                  Remarks
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Optional notes..."
                  value={myReportForm.remarks}
                  onChange={(e) => setMyReportForm({ ...myReportForm, remarks: e.target.value })}
                  style={{ width: "100%", fontSize: 13 }}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, borderTop: "1px solid var(--line)", paddingTop: 14 }}>
                <button
                  type="button"
                  className="outline-btn"
                  onClick={() => setShowSubmitModal(false)}
                  disabled={submittingReport}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="primary-btn"
                  disabled={submittingReport}
                  data-testid="save-daily-report-btn"
                >
                  {submittingReport ? "Saving…" : "Save Report"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Dedicated Employee Detail Modal
 */
function IndividualDailyReportModal({ user, reportDate, onClose }) {
  const rm = user?.role_metrics || {};
  const rep = user?.daily_report;
  const isSubmitted = user?.daily_report_status === "Submitted";
  const userDeptsLower = (user?.departments || [user?.department || ""]).map((d) => (d || "").toLowerCase());

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(15, 23, 42, 0.6)",
        zIndex: 1000,
        display: "grid",
        placeItems: "center",
        padding: 16,
      }}
      data-testid="individual-daily-report-modal"
    >
      <div
        style={{
          background: "#ffffff",
          borderRadius: 8,
          width: "100%",
          maxWidth: 780,
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2)",
          border: "1px solid var(--line)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: "16px 22px",
            borderBottom: "1px solid var(--line)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "#f8fafc",
          }}
        >
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <strong style={{ fontSize: 17, color: "var(--navy)" }}>{user?.name}</strong>
              <span className="badge blue">{user?.department}</span>
              <span
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  color: user?.role === "super_admin" ? "var(--blue)" : "var(--muted)",
                  textTransform: "uppercase",
                }}
              >
                {user?.role}
              </span>
            </div>
            <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 3 }}>
              {user?.email} · Date: <strong>{formatDateDisplay(reportDate)}</strong>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span className={`badge ${isSubmitted ? "green" : "amber"}`}>
              {isSubmitted ? "✓ Submitted" : "Pending"}
            </span>
            <button
              onClick={onClose}
              style={{ background: "none", border: "none", color: "var(--muted)", cursor: "pointer", padding: 4 }}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div style={{ padding: "20px 22px", display: "flex", flexDirection: "column", gap: 18 }}>
          {/* 1. Today's Recorded Activity on selected date */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", marginBottom: 8, letterSpacing: 0.5 }}>
              Today's Activity ({formatDateDisplay(reportDate)})
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(105px, 1fr))", gap: 8 }}>
              <div style={{ background: "#f8fafc", padding: "10px 12px", borderRadius: 6, border: "1px solid var(--line)" }}>
                <div style={{ fontSize: 10.5, color: "var(--muted)" }}>Calls Logged</div>
                <strong style={{ fontSize: 16, color: "var(--navy)" }}>{user?.calls_made ?? 0}</strong>
              </div>
              <div style={{ background: "#f8fafc", padding: "10px 12px", borderRadius: 6, border: "1px solid var(--line)" }}>
                <div style={{ fontSize: 10.5, color: "var(--muted)" }}>Leads Created</div>
                <strong style={{ fontSize: 16, color: "var(--navy)" }}>{user?.leads_created ?? user?.new_leads ?? 0}</strong>
              </div>
              <div style={{ background: "#f8fafc", padding: "10px 12px", borderRadius: 6, border: "1px solid var(--line)" }}>
                <div style={{ fontSize: 10.5, color: "var(--muted)" }}>Contacted</div>
                <strong style={{ fontSize: 16, color: "var(--navy)" }}>{user?.leads_contacted ?? 0}</strong>
              </div>
              <div style={{ background: "#f8fafc", padding: "10px 12px", borderRadius: 6, border: "1px solid var(--line)" }}>
                <div style={{ fontSize: 10.5, color: "var(--muted)" }}>Qualified</div>
                <strong style={{ fontSize: 16, color: user?.leads_qualified > 0 ? "var(--green)" : "var(--navy)" }}>
                  {user?.leads_qualified ?? 0}
                </strong>
              </div>
              <div style={{ background: "#f8fafc", padding: "10px 12px", borderRadius: 6, border: "1px solid var(--line)" }}>
                <div style={{ fontSize: 10.5, color: "var(--muted)" }}>Won (Today)</div>
                <strong style={{ fontSize: 16, color: (user?.leads_won || user?.leads_converted) > 0 ? "var(--green)" : "var(--navy)" }}>
                  {user?.leads_won ?? user?.leads_converted ?? 0}
                </strong>
              </div>
              <div style={{ background: "#f8fafc", padding: "10px 12px", borderRadius: 6, border: "1px solid var(--line)" }}>
                <div style={{ fontSize: 10.5, color: "var(--muted)" }}>Lost (Today)</div>
                <strong style={{ fontSize: 16, color: user?.leads_lost > 0 ? "var(--red)" : "var(--navy)" }}>
                  {user?.leads_lost ?? 0}
                </strong>
              </div>
              <div style={{ background: "#f8fafc", padding: "10px 12px", borderRadius: 6, border: "1px solid var(--line)" }}>
                <div style={{ fontSize: 10.5, color: "var(--muted)" }}>Follow-ups Done</div>
                <strong style={{ fontSize: 16, color: "var(--navy)" }}>{user?.followups_completed ?? 0}</strong>
              </div>
              <div style={{ background: "#f8fafc", padding: "10px 12px", borderRadius: 6, border: "1px solid var(--line)" }}>
                <div style={{ fontSize: 10.5, color: "var(--muted)" }}>Deliveries</div>
                <strong style={{ fontSize: 16, color: user?.deliveries_completed > 0 ? "var(--green)" : "var(--navy)" }}>
                  {user?.deliveries_completed ?? 0}
                </strong>
              </div>
              <div style={{ background: "#f8fafc", padding: "10px 12px", borderRadius: 6, border: "1px solid var(--line)" }}>
                <div style={{ fontSize: 10.5, color: "var(--muted)" }}>Tasks Done</div>
                <strong style={{ fontSize: 16, color: "var(--navy)" }}>{user?.tasks_completed ?? 0}</strong>
              </div>
            </div>
          </div>

          {/* 2. Current Pipeline & Assigned Queue (Distinct from Daily Activity) */}
          {userDeptsLower.includes("sales") && (
            <div style={{ border: "1px solid #bfdbfe", background: "#f8fafc", padding: "14px 16px", borderRadius: 6 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: "var(--blue)" }}>
                  Current Lead Pipeline (Assigned to {user?.name})
                </div>
                <span className="badge blue" style={{ fontSize: 11 }}>
                  {user?.pipeline?.total_leads || 0} Total Active Leads
                </span>
              </div>

              {/* Status pills breakdown */}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                <div style={{ background: "#ecfdf5", border: "1px solid #a7f3d0", padding: "6px 12px", borderRadius: 6, fontSize: 12 }}>
                  <span style={{ color: "#047857", fontWeight: 700 }}>Won: </span>
                  <strong style={{ color: "#065f46" }}>{user?.pipeline?.won || 0}</strong>
                  <span style={{ color: "#047857", fontSize: 11, marginLeft: 4 }}>
                    ({money(user?.pipeline?.won_value || 0)})
                  </span>
                </div>

                <div style={{ background: "#fef3c7", border: "1px solid #fde68a", padding: "6px 12px", borderRadius: 6, fontSize: 12 }}>
                  <span style={{ color: "#92400e", fontWeight: 700 }}>Qualified: </span>
                  <strong style={{ color: "#78350f" }}>{user?.pipeline?.qualified || 0}</strong>
                  <span style={{ color: "#92400e", fontSize: 11, marginLeft: 4 }}>
                    ({money(user?.pipeline?.qualified_value || 0)})
                  </span>
                </div>

                <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", padding: "6px 12px", borderRadius: 6, fontSize: 12 }}>
                  <span style={{ color: "#1d4ed8", fontWeight: 600 }}>Contacted: </span>
                  <strong>{user?.pipeline?.contacted || 0}</strong>
                </div>

                <div style={{ background: "#fefce8", border: "1px solid #fef08a", padding: "6px 12px", borderRadius: 6, fontSize: 12 }}>
                  <span style={{ color: "#854d0e", fontWeight: 600 }}>Interested / Proposal: </span>
                  <strong>{user?.pipeline?.interested || 0}</strong>
                </div>

                <div style={{ background: "#f8fafc", border: "1px solid var(--line)", padding: "6px 12px", borderRadius: 6, fontSize: 12 }}>
                  <span style={{ color: "var(--muted)", fontWeight: 600 }}>New: </span>
                  <strong>{user?.pipeline?.new || 0}</strong>
                </div>

                {user?.pipeline?.not_interested > 0 && (
                  <div style={{ background: "#fef2f2", border: "1px solid #fecaca", padding: "6px 12px", borderRadius: 6, fontSize: 12 }}>
                    <span style={{ color: "#b91c1c", fontWeight: 600 }}>Not Interested: </span>
                    <strong>{user?.pipeline?.not_interested}</strong>
                  </div>
                )}

                {user?.pipeline?.lost > 0 && (
                  <div style={{ background: "#fef2f2", border: "1px solid #fecaca", padding: "6px 12px", borderRadius: 6, fontSize: 12 }}>
                    <span style={{ color: "#b91c1c", fontWeight: 600 }}>Lost: </span>
                    <strong>{user?.pipeline?.lost}</strong>
                  </div>
                )}
              </div>

              <div style={{ display: "flex", gap: 16, fontSize: 12, color: "var(--muted)", borderTop: "1px solid #e2e8f0", paddingTop: 8 }}>
                <div>
                  Open Pipeline Value: <strong style={{ color: "var(--navy)" }}>{money(user?.pipeline?.open_value || 0)}</strong>
                </div>
                <div>
                  Follow-ups Due (Today): <strong style={{ color: "var(--navy)" }}>{user?.pipeline?.followups_due || 0}</strong>
                </div>
                {(user?.pipeline?.followups_overdue || 0) > 0 && (
                  <div style={{ color: "var(--red)" }}>
                    Overdue Follow-ups: <strong>{user?.pipeline?.followups_overdue}</strong>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 3. Employee Self-Reported Daily Report */}
          <div
            style={{
              padding: "16px 18px",
              background: isSubmitted ? "#f0fdf4" : "#fffbeb",
              border: `1px solid ${isSubmitted ? "#bbf7d0" : "#fde68a"}`,
              borderRadius: 6,
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 13, color: isSubmitted ? "#166534" : "#92400e", marginBottom: 8 }}>
              Self-Reported Work Summary
            </div>

            {isSubmitted ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 10, fontSize: 13 }}>
                <div>
                  <div style={{ color: "var(--navy)", lineHeight: 1.5 }}>{rep?.work_summary}</div>
                </div>
                {rep?.tasks_completed_summary && (
                  <div style={{ borderTop: "1px solid #dcfce7", paddingTop: 8 }}>
                    <span style={{ fontWeight: 600, color: "#166534" }}>Completed: </span>
                    <span style={{ color: "#334155" }}>{rep.tasks_completed_summary}</span>
                  </div>
                )}
                {rep?.tasks_pending_summary && (
                  <div>
                    <span style={{ fontWeight: 600, color: "#b45309" }}>Pending / Next: </span>
                    <span style={{ color: "#334155" }}>{rep.tasks_pending_summary}</span>
                  </div>
                )}
                {rep?.blockers && (
                  <div style={{ background: "#fef2f2", padding: "8px 12px", borderRadius: 4, color: "#991b1b" }}>
                    <strong>Blocker:</strong> {rep.blockers}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ fontSize: 12.5, color: "#92400e" }}>
                No daily report submitted for this date.
              </div>
            )}
          </div>

          {/* 4. Department-Specific Performance */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", marginBottom: 8, letterSpacing: 0.5 }}>
              Workflow Details
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10, fontSize: 12.5 }}>
              {userDeptsLower.includes("sales") && (
                <div style={{ border: "1px solid var(--line)", padding: "12px", borderRadius: 6, background: "#fff" }}>
                  <div style={{ fontWeight: 700, color: "var(--blue)", marginBottom: 6 }}>Sales Breakdown</div>
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}><span>Leads Assigned:</span> <strong>{rm.leads_assigned ?? 0}</strong></div>
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}><span>Follow-ups Done:</span> <strong>{rm.followups_completed ?? 0}</strong></div>
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}><span>Overdue Follow-ups:</span> <strong style={{ color: (rm.followups_overdue || 0) > 0 ? "var(--red)" : "inherit" }}>{rm.followups_overdue ?? 0}</strong></div>
                </div>
              )}
              {userDeptsLower.includes("delivery") && (
                <div style={{ border: "1px solid var(--line)", padding: "12px", borderRadius: 6, background: "#fff" }}>
                  <div style={{ fontWeight: 700, color: "var(--green)", marginBottom: 6 }}>Delivery Breakdown</div>
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}><span>Assigned:</span> <strong>{rm.orders_assigned_for_delivery ?? 0}</strong></div>
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}><span>Pending:</span> <strong>{rm.deliveries_pending ?? 0}</strong></div>
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}><span>Challans Done:</span> <strong>{rm.challans_completed ?? 0}</strong></div>
                </div>
              )}
              {userDeptsLower.includes("production") && (
                <div style={{ border: "1px solid var(--line)", padding: "12px", borderRadius: 6, background: "#fff" }}>
                  <div style={{ fontWeight: 700, color: "var(--amber)", marginBottom: 6 }}>Production Breakdown</div>
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}><span>Batches Assigned:</span> <strong>{rm.production_tasks_assigned ?? 0}</strong></div>
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}><span>Batches Done:</span> <strong>{rm.production_tasks_completed ?? 0}</strong></div>
                </div>
              )}
              {userDeptsLower.includes("design") && (
                <div style={{ border: "1px solid var(--line)", padding: "12px", borderRadius: 6, background: "#fff" }}>
                  <div style={{ fontWeight: 700, color: "#9333ea", marginBottom: 6 }}>Design Breakdown</div>
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}><span>Designs Done:</span> <strong>{rm.designs_completed ?? 0}</strong></div>
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}><span>Awaiting Approval:</span> <strong>{rm.designs_awaiting_approval ?? 0}</strong></div>
                </div>
              )}
              <div style={{ border: "1px solid var(--line)", padding: "12px", borderRadius: 6, background: "#fff" }}>
                <div style={{ fontWeight: 700, color: "var(--muted)", marginBottom: 6 }}>General Tasks</div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}><span>Assigned:</span> <strong>{rm.tasks_assigned ?? 0}</strong></div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}><span>Completed:</span> <strong>{rm.tasks_completed ?? 0}</strong></div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}><span>Overdue:</span> <strong style={{ color: (rm.overdue_tasks || 0) > 0 ? "var(--red)" : "inherit" }}>{rm.overdue_tasks ?? 0}</strong></div>
              </div>
            </div>
          </div>

          {/* Today's Activity Log */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", marginBottom: 8, letterSpacing: 0.5 }}>
              Activity Log ({user?.activities?.length || 0})
            </div>
            {(!user?.activities || user.activities.length === 0) ? (
              <div style={{ padding: "18px", textAlign: "center", color: "var(--muted)", background: "#f8fafc", borderRadius: 6, fontSize: 12 }}>
                No activity recorded today.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 180, overflowY: "auto" }}>
                {user.activities.map((act, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "8px 12px",
                      background: "#f8fafc",
                      border: "1px solid #f1f5f9",
                      borderRadius: 6,
                      fontSize: 12,
                    }}
                  >
                    <div>
                      <strong style={{ color: "var(--navy)" }}>{act.action}</strong>
                      {act.entity_name && <span style={{ color: "var(--muted)", marginLeft: 6 }}>({act.entity_name})</span>}
                    </div>
                    <span style={{ color: "var(--muted)", fontSize: 11 }}>{act.formatted_time}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div
          style={{
            padding: "12px 22px",
            borderTop: "1px solid var(--line)",
            display: "flex",
            justifyContent: "flex-end",
            background: "#f8fafc",
          }}
        >
          <button className="outline-btn" onClick={onClose} style={{ padding: "6px 16px" }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
